import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Download } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useResolveCrTagToSub } from '@/hooks/useResolveCrTagToSub';
import { useProfileByTagQuery } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { PlayerSocialButtons } from '@/components/social/PlayerSocialButtons';
import { crDeckImportUrl } from '@/lib/crDeckLink';

/**
 * Public CR profile for any player tag — reached from a battle opponent
 * ("View CR profile"). qbot `GET /profile/:tag` is public-by-tag, so this
 * works for players who are NOT QLASH users.
 *
 * Social (identity bridge): a CR tag resolves to a Logto `sub` — QBot
 * (`GET /api/v1/cr/players/by-tag/:tag`) first, qduels
 * (`GET /v1/players/by-cr-tag/:tag`) as fallback — for players QLASH has
 * mapped. When it resolves we render follow / add-friend; otherwise (both
 * miss, or a non-QLASH player) we keep stats-only and fire no social API calls.
 */
export function CRPlayerProfile() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const { tag = '' } = useParams();

  const { data, isLoading, error } = useProfileByTagQuery({ tag }, { skip: !tag });

  // Identity bridge: CR tag → Logto sub (QBot primary, qduels fallback). When
  // neither resolves → `undefined` → no social buttons (and no follow/friend
  // reads fire downstream).
  const { logtoUserId: socialUserId } = useResolveCrTagToSub(tag);

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('player.loading')}</div>;
  }
  if (error || !data?.player) {
    return <div className="px-4 py-6 text-sm text-danger">{t('player.error')}</div>;
  }

  const { player } = data;
  const stats: Array<{ label: string; value: string }> = [
    { label: t('player.stats.trophies'), value: player.trophies.toLocaleString() },
    { label: t('player.stats.best'), value: player.bestTrophies.toLocaleString() },
    { label: t('player.stats.wins'), value: player.wins.toLocaleString() },
    { label: t('player.stats.losses'), value: player.losses.toLocaleString() },
    { label: t('player.stats.threeCrowns'), value: player.threeCrownWins.toLocaleString() },
    { label: t('player.stats.level'), value: String(player.expLevel) },
  ];

  const handleImport = () => {
    haptics.impact('light');
    openLink.ifAvailable(crDeckImportUrl(player.currentDeck));
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      <header className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand">
          <Crown className="h-7 w-7" strokeWidth={2.5} />
        </div>
        <div className="flex flex-1 flex-col min-w-0">
          <h1 className="truncate text-lg font-bold text-fg">{player.name}</h1>
          <span className="text-[11px] text-fg-muted tabular-nums">{player.tag}</span>
          <span className="mt-0.5 text-[11px] text-fg-muted">{player.arena?.name ?? '—'}</span>
          {socialUserId && (
            <PlayerSocialButtons userId={socialUserId} interactive className="mt-2" />
          )}
        </div>
      </header>

      <section className="grid grid-cols-3 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl bg-bg-secondary p-3 text-center">
            <p className="text-base font-bold tabular-nums text-fg">{s.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-fg-muted">{s.label}</p>
          </div>
        ))}
      </section>

      {player.currentDeck?.length === 8 && (
        <section className="rounded-xl bg-bg-secondary p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
              {t('player.currentDeck')}
            </h2>
            <button
              type="button"
              onClick={handleImport}
              className="flex shrink-0 items-center gap-1 rounded-md bg-bg-tertiary px-2.5 py-1 text-[11px] font-semibold text-brand transition-opacity active:opacity-80"
            >
              <Download className="h-3.5 w-3.5" />
              {t('player.import')}
            </button>
          </div>
          <DeckGrid cards={player.currentDeck} showLevels={false} />
        </section>
      )}
    </div>
  );
}
